import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import DataTable from "react-data-table-component";
import { useDispatch, useSelector } from "react-redux";
import { addRole, fetchRoles, updateRole, deleteRole } from "../../../slices/admin/rolesSlice";
import Switch from 'react-switch'; // You can install react-bootstrap for Switch, or use any other switch component
import { Delete, Pen } from "lucide-react";
import Swal from "sweetalert2";
import { customTableStyles } from "../../../utils/tableStylings";

const AdminRoles = () => {
    const dispatch = useDispatch();
    const { roles } = useSelector((state) => state.adminRoles) || {};
    const [search, setSearch] = useState("");
    const [loading, setLoading] = useState(false);
    const [selectedRole, setSelectedRole] = useState([]);
    const [roleName, setRoleName] = useState("");
    const [slug, setSlug] = useState("");

    // Columns configuration for DataTable
    const columns = [
        {
            name: "#", 
            selector: (row, index) => index + 1, // Add serial number starting from 1
            sortable: false, // No need to sort by S.No
            width: "50px" // Optional: set a fixed width for the column
        },
        { name: "Role Name", selector: (row) => row.name, sortable: true },
        { name: "Slug", selector: (row) => row.slug, sortable: true },
        { name: "Status", 
            cell: row => (
                <Switch
                    checked={row.status == 1}  // Assuming 'active' is true status and 'inactive' is false
                    onChange={(checked) => handleStatusToggle(row.id, checked)}
                    offColor="#d6d6d6"
                    onColor="#4caf50"
                    offHandleColor="#888"
                    onHandleColor="#fff"
                    uncheckedIcon={false}
                    checkedIcon={false}
                />
            ),
            sortable: true
        },
        {
            name: "Actions",
            button: true,
            cell: (row) => (
                <div>
                    <Link
                        className="text-success me-2"
                        onClick={() => handleEdit(row)}
                    >
                        <Pen />
                    </Link>
                    <Link
                        className="text-danger btn-sm"
                        onClick={() => handleDelete(row.id)}
                    >
                        <Delete />
                    </Link>
                </div>
            ),
            ignoreRowClick: true,
            allowOverflow: true,
            button: true
        },
    ];

    useEffect(() => {
        dispatch(fetchRoles());
    }, [dispatch]);

    const handleFormSubmit = (e) => {
        e.preventDefault();
        const roleData = {
            name: roleName,
            slug,
            status: 1, // Defaulting to active
        };
        
        if (selectedRole?.id) {
            // Update existing role
            dispatch(updateRole({ id: selectedRole.id, roleData: roleData }));
        } else {
            // Add new role
            dispatch(addRole(roleData));
            
        }
        setSelectedRole([]);
        setRoleName('');
        setSlug('');
    };
    
    const generateSlug = (name) => {
        return name
            .toLowerCase()
            .replace(/\s+/g, '-')      // Replace spaces with hyphens
            .replace(/[^\w\-]+/g, '')  // Remove non-alphanumeric characters
            .replace(/\-\-+/g, '-')    // Replace multiple hyphens with a single one
            .replace(/^-+/, '')        // Remove leading hyphens
            .replace(/-+$/, '');       // Remove trailing hyphens
    };

    // Update the slug when roleName changes
    useEffect(() => {
        if (roleName) {
            const generatedSlug = generateSlug(roleName);
            setSlug(generatedSlug);
        }
    }, [roleName]);

    const handleStatusToggle = (id, checked) => {
        // Dispatch an action to update the company status
        dispatch(updateRole({ 
            id: id, 
            roleData: { status: checked }  // Correct the formData to be an object, not an array
        }));
    };


    // Handle role edit
    const handleEdit = (role) => {
        setSelectedRole(role);
        setRoleName(role.name);
        setSlug(role.slug);
    };

    // Handle role delete
    const handleDelete = (id) => {

        Swal.fire({
            title: '<strong>Are you sure?</strong>',
            html: `<p>This action will <b>permanently delete</b> this company.</p>`,
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#d33',
            cancelButtonColor: '#3085d6',
            confirmButtonText: 'Yes, delete it!',
            cancelButtonText: 'Cancel',
        }).then((result) => {
            if (result.isConfirmed) {
                dispatch(deleteRole(id));
                Swal.fire({
                    icon: 'success',
                    title: 'Deleted!',
                    text: 'The Role has been deleted.',
                    toast: true,          // Enable toast style
                    position: 'top-end',  // Position at the top-right corner
                    showConfirmButton: false,  // Hide the confirm button
                    timer: 3000,          // Set the timer to auto-close after 3 seconds
                    timerProgressBar: true,  // Show the progress bar for the timer
                    didOpen: (toast) => {
                        toast.addEventListener('mouseenter', Swal.stopTimer);
                        toast.addEventListener('mouseleave', Swal.resumeTimer);
                    }
                });
                
            }
            
        });

    };

    return (
        <div className="row">
            <div className="col-8">
                <div className="card">
                    <div className="card-header d-flex justify-content-between align-items-center">
                        <h4 className="card-title">Roles</h4>
                    </div>
                    <div className="card-body">
                        <input
                            type="text"
                            placeholder="Search Roles"
                            className="form-control mb-3"
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                        />
                        <div className="table-responsive">
                            <DataTable
                                className="table-hover custom-table"
                                columns={columns}
                                data={roles} // Use filtered data
                                pagination
                                striped
                                progressPending={loading}
                                customStyles={customTableStyles}
                            />
                        </div>
                    </div>
                </div>
            </div>

            <div className="col-4">
                <div className="card">
                    <div className="card-header">
                        <h5 className="card-title">
                            {selectedRole?.name ? "Edit Role" : "Add New Role"}
                        </h5>
                    </div>
                    <div className="card-body">
                        <form onSubmit={handleFormSubmit}>
                            <div className="mb-3">
                                <label htmlFor="roleName" className="form-label">
                                    Role Name
                                </label>
                                <input
                                    type="text"
                                    className="form-control"
                                    id="roleName"
                                    value={roleName}
                                    onChange={(e) => setRoleName(e.target.value)}
                                    required
                                />
                            </div>
                            <div className="mb-3">
                                <label htmlFor="slug" className="form-label">
                                    Slug
                                </label>
                                <input
                                    type="text"
                                    className="form-control"
                                    id="slug"
                                    value={slug}
                                    onChange={(e) => setSlug(e.target.value)}
                                    required
                                />
                            </div>
                            <div className="mb-3">
                                <button type="submit" className="btn btn-primary">
                                    {selectedRole?.name ? 'Update' : 'Submit'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default AdminRoles;
